import { MovieClip } from '@play-co/timestep-core/ui';
import { Container, Vector2 } from '../../../odie';

import { app } from '../../app';
import { AxisId } from '../../defs/block';
import { BlockEntity } from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { mapGetPan } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { MergeEffect } from './MergeEffect';
import { RocketEffect } from './RocketEffect';

// types
//-----------------------------------------------------------------------------
export type BombRocketComboEffectOptions = {
  subject: BlockEntity;
  pair: BlockEntity[];
  all: BlockEntity[];
};

/*
    bomb+rocket combo multi launch effect
*/
export class BombRocketComboEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: BombRocketComboEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: BombRocketComboEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const effectPromises: Promise<void>[] = [];

    // play sound
    app.sound.play('puzzle-combo-rocket-bomb', {
      pan: mapGetPan(this._options.subject.c.position.mapPosition),
    });

    // merge all blocks
    await new MergeEffect(
      this._scene,
      this._options.all,
      this._options.subject.c.position.mapPosition,
    ).execute();

    // Execute a big rocket effect in each direction.
    const position = this._options.subject.c.position.mapPosition;
    const animation = new MovieClip({
      url: getMatch2AssetPath('blocks/power/rocket'),
      ...position,
      fps: 40,
    });

    // spawn animation as overlay entity
    const entity = spawnOverlayEntity(
      this._scene,
      position,
      { width: 1, height: 1 },
      () => new Container(animation),
      'foreground',
    );

    // Play big rocket spawn animation.
    await animation.playAsync('rocket_launch_big');

    despawnOverlayEntity(this._scene, entity);

    this._executeRocketEffect(effectPromises, 0, 0, 'vertical');
    this._executeRocketEffect(effectPromises, 0, 0, 'horizontal');

    await Promise.all(effectPromises);
  }

  // private: support
  //-------------------------------------------------------------------------
  private _executeRocketEffect(
    effectPromises: Promise<void>[],
    x: number,
    y: number,
    axis: AxisId,
  ) {
    // get base position
    const basePosition = this._options.subject.c.position.mapPosition;

    // get launch position
    const position = new Vector2(basePosition.x + x, basePosition.y + y);

    // if valid map position
    if (this._scene.sessionEntity.c.map.isPositionOnMap(position)) {
      // create and execute rocket effect
      const effect = new RocketEffect(this._scene, {
        position,
        axis,
        big: true,
      }).execute();

      // add to effect promises
      effectPromises.push(effect);
    }
  }
}
